import gql from 'graphql-tag';
import graphqlClient from '../helpers/graphqlClient';

export default new (class {
	async getSetup(studentId) {
		const { data } = await graphqlClient.query({
			query: gql`
                {
                    students(where: "id=${studentId}") {
                        currentCreditScoreMessage
                        grossMonthlyIncome
                        studentInterests {
                            cmsInterest {
                                name
                            }
                        }
                        federalIncomeTax {
                            key
                            value
                        }
                        socialSecurityTax {
                            key
                            value
                        }
                        medicareTax {
                            key
                            value
                        }
                    }
                }`,
			fetchPolicy: 'no-cache'
		});

		return data.students[0];
	}
	async getData(studentId, lifeStageId) {
		const { data } = await graphqlClient.query({
			query: gql`
                {
                    students(where: "id=${studentId}") {
                        id
                        lifeStage_ID
                        autoLoanAmount
                        autoLoanDealershipAmount
                        isApprovedForMortgageLoan
                        mortgageLoanAmount
                        totalExpenses
                        showLifeEventPopUp
                        hideLifeStageSummaryPopup
                        hideHopesAndDreamsPopup
                        hideOnboardingTutorialPopup
                        hideCreditCardPaymentPopup
                        hideLoanPaymentPopup
                        hideLifeStageCompletionPopup
												hideForcedLifeStageCompletionPopup
                        creditCardDebt
												creditCardInterest
												isCurrentLifeStageForced
												isTransferToNextLifeStagePending
                        nextLifeStageID
                        formStudentName
                        studentSmileScoreData{
                            score
                            message
                        }

                        currentLifeStageCompletionPercentage
                        minimumStudentCreditCardPayment
                        mandatoryCategoriesCompletionPercentage
                        studentGoals {
                            id
                            goal_Id
                            totalSavingsAmount
                            cmsGoal {
                                  name
								  								description
                            }
                            studentGoalSavings(where: "lifeStage_Id=\\"${lifeStageId}\\""){
                                id
                                  amount
                                totalAmount
                            }
                        }
                    }
                }
                `,
			fetchPolicy: 'no-cache'
		});

		return data.students[0];
	}
	async getLifeStageName(lifeStageId) {
		const { data } = await graphqlClient.query({
			query: gql`
				query lifeStageName($whereLifeStageId: String!) {
					cmsLifeStages(where: $whereLifeStageId) {
						name
					}
				}
			`,
			variables: {
				whereLifeStageId: `lifeStage_ID = "${lifeStageId}"`
			},
			fetchPolicy: 'no-cache'
		});

		return data.cmsLifeStages[0].name;
	}

	async getLifeStagePaymentSummary(studentId) {
		const { data } = await graphqlClient.query({
			query: gql`
      {
        students(where: "id=${studentId}") {
          studentLoanDebt
          totalStudentLoan
          totalExpenses
          minimumStudentLoanPayment
          currentLifeStageTotalCreditCardPayment
          currentLifeStageTotalStudentLoanPayment
          studentGoals {
            totalSavingsIncludingCurrentLifeStage
            cmsGoal {
              name
			  description
            }
          }
        }
      }
      `,
			fetchPolicy: 'no-cache'
		});

		return data.students[0];
	}
	async getCreditCardExpensesForCurrentLifeStage(studentId, lifeStageId) {
		const creditCardUpperPaymentMethod_ID = 1;
		const { data } = await graphqlClient.query({
			query: gql`
				query creditCardExpenses($where: String!) {
					studentExpenseSubCategoryExpenditures(where: $where) {
						lifeStage_ID
						upperPaymentMethod_ID
						amountSpent
					}
				}
			`,
			variables: {
				where: `lifeStage_ID = "${lifeStageId}" && studentId = "${studentId}" && upperPaymentMethod_ID = ${creditCardUpperPaymentMethod_ID} `
			},
			fetchPolicy: 'no-cache'
		});
		const summedExpenses = data.studentExpenseSubCategoryExpenditures.reduce(
			(a, b) => {
				return a + b.amountSpent;
			},
			0
		);
		return summedExpenses;
	}
	async endLifeStage(studentId, nextLifeStage) {
		const response = await graphqlClient.mutate({
			mutation: gql`
				mutation($update: StudentInputObjectGraphType!) {
					updateIgnoreNullValuesStudent(update: $update) {
						id
					}
				}
			`,
			variables: {
				update: {
					id: studentId,
					hideHopesAndDreamsPopup: !nextLifeStage, // show hopes and dreams modal if there is going to be another life stage
					hideLifeStageSummaryPopup: true,
					showKnowledgeQuestionPage: true
				}
			}
		});

		return response;
	}

	async getStudentCreditCardPayments(studentId, lifeStageId) {
		const filters = [];
		const studentFilters = [];

		filters.push(`studentId = ${studentId}`);
		filters.push(`lifeStage_ID = "${lifeStageId}"`);
		filters.push('paymentType="CreditCard"');

		studentFilters.push(`id=${studentId}`);

		const { data } = await graphqlClient.query({
			query: gql`
				query CreditCardPayments($where: String!, $studentsWhere: String!) {
					studentPayments(where: $where) {
						payedAmount
						lifeStage_ID
						studentId
						paymentType
					}
					creditCardDebt: students(where: $studentsWhere) {
						debt: creditCardDebt
						id
					}
				}
			`,
			fetchPolicy: 'no-cache',
			variables: {
				where: filters.join('&&'),
				studentsWhere: studentFilters.join('&&')
			}
		});

		if (data)
			return {
				debt: data.creditCardDebt[0].debt,
				payedAmount: data.studentPayments[0]
					? data.studentPayments[0].payedAmount
					: 0
			};
	}
	async getSimCompleteLifeStageStatus(simulationId) {
		const { data } = await graphqlClient.query({
			query: gql`
				query CanCompleteLifeStage($where: String!) {
					simulations(where: $where) {
						isCompleteLifeStageOptionEnabled
					}
				}
			`,
			variables: {
				where: `id=${simulationId}`
			}
		});

		if (data.simulations.length) {
			return data.simulations[0].isCompleteLifeStageOptionEnabled;
		}
		// Enabled (true) is the default option
		return true;
	}

	async updateBudgetModalVisibility(studentId, visibility) {
		return await graphqlClient.mutate({
			mutation: gql`
				mutation($update: StudentInputObjectGraphType!) {
					updateStudent(update: $update) {
						id
					}
				}
			`,
			variables: {
				update: {
					id: studentId,
					hideConfirmBudgetPopup: visibility
				}
			}
		});
	}
})();
